import { Button } from '@/components/shadcn/button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { useRestClient, RestSchemas } from '@/contexts/QueryProvider'
import { MessagesStorageStorageFileType } from '@/util/enums'
import { useSessionStorage } from '@/hooks/useSessionStorage'
import { isPlatformAdmin, isSuperUser } from '@/util'
import { IconDeleteOutline } from '@icons/IconDeleteOutline'
import { IconDownloadOutline } from '@icons/IconDownloadOutline'
import { toast } from 'sonner'
import { useCurrentWorkspace } from '../../useCurrentWorkspace'
import { downloadFile } from './downloadFile'
import { FileCard } from './FileCard'

type MessagesStorageStorageDetailItem = RestSchemas['Messages.Storage.StorageDetailItem']
type MessagesWorkspacesEntity = RestSchemas['Messages.Workspaces.Entity']

type EntityFilesListProps = {
  selectedEntity: MessagesWorkspacesEntity
}

export const DataAssetsFilesList = (props: EntityFilesListProps) => {
  const { workspaceId } = useCurrentWorkspace()

  const [userRole] = useSessionStorage('roles', 1)
  const isDeleteFileAllowed = isPlatformAdmin(userRole) || isSuperUser(userRole)

  const { getStorageApiGetFilesDetailsEntityDataAssets, postStorageApiDeleteFileWorkspace } = useRestClient()

  const { data: dataAssets } = getStorageApiGetFilesDetailsEntityDataAssets.useSuspenseQuery({
    query: {
      entityId: props.selectedEntity?.entityId,
    },
  })

  const { mutate: deleteFile, isPending: isDeleteFilePending } = postStorageApiDeleteFileWorkspace.useMutation()

  const onDeleteClick = async (f: MessagesStorageStorageDetailItem) => {
    deleteFile(
      {
        query: {
          blobName: f.encryptedBlobName,
          subscriptionId: workspaceId,
          fileType: MessagesStorageStorageFileType.Result,
        },
      },
      {
        onSuccess: () => {
          toast.success('File deleted.')
          getStorageApiGetFilesDetailsEntityDataAssets.invalidateQueries({
            parameters: {
              query: {
                entityId: props.selectedEntity?.entityId,
              },
            },
          })
        },
        onError: () => {
          toast.error('File was not deleted.')
        },
      }
    )
  }

  const onDownloadClick = (f: MessagesStorageStorageDetailItem) => {
    downloadFile(f, workspaceId)
  }

  return (
    <div className="flex flex-col gap-2 pb-8">
      <div>{props.selectedEntity?.name} Files</div>
      {dataAssets.length === 0 && <div>No files found.</div>}
      {dataAssets.map((f) => {
        const actions = (
          <>
            <Button variant="ghost" size="icon" onClick={() => onDownloadClick(f)}>
              <IconDownloadOutline />
            </Button>
            <ConfirmDialog
              trigger={
                <Button disabled={!isDeleteFileAllowed || isDeleteFilePending} variant="ghost" size="icon">
                  <IconDeleteOutline />
                </Button>
              }
              onConfirm={() => onDeleteClick(f)}
              title="Delete file"
              description="Are you sure you want to delete the file?"
            />
          </>
        )
        return <FileCard key={f.encryptedBlobName} file={f} actions={actions} />
      })}
    </div>
  )
}
